import { useState, useEffect, useRef } from "react";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface CryptoWallet {
  id: number;
  coin_name: string;
  coin_symbol: string;
  wallet_address: string;
  network: string;
  qr_code_url: string | null;
  created_at: string;
}

export default function AdminWalletsPage() {
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ coin_name: "", coin_symbol: "", wallet_address: "", network: "" });
  const [addStatus, setAddStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [addError, setAddError] = useState("");
  const [addQrFile, setAddQrFile] = useState<File | null>(null);
  const [addQrPreview, setAddQrPreview] = useState<string | null>(null);
  const addFileRef = useRef<HTMLInputElement>(null);
  const [editingWallet, setEditingWallet] = useState<CryptoWallet | null>(null);
  const [editForm, setEditForm] = useState({ coin_name: "", coin_symbol: "", wallet_address: "", network: "" });
  const [editStatus, setEditStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [editError, setEditError] = useState("");
  const [editQrFile, setEditQrFile] = useState<File | null>(null);
  const [editQrPreview, setEditQrPreview] = useState<string | null>(null);
  const editFileRef = useRef<HTMLInputElement>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [viewingQr, setViewingQr] = useState<string | null>(null);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase.from("crypto_wallets").select("*").order("id", { ascending: true });
      setWallets(data || []);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  const uploadQrCode = async (file: File): Promise<string> => {
    const supabase = getSupabase();
    const fileName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
    const { data, error } = await supabase.storage
      .from("wallet-qr")
      .upload(fileName, file, { cacheControl: "3600", upsert: true });

    if (error) throw error;

    const { data: urlData } = supabase.storage.from("wallet-qr").getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleAddQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAddQrFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAddQrPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!addForm.coin_name || !addForm.coin_symbol || !addForm.wallet_address) return;
    setAddStatus("loading");
    setAddError("");
    try {
      let qrUrl: string | null = null;
      if (addQrFile) {
        qrUrl = await uploadQrCode(addQrFile);
      }

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("crypto_wallets")
        .insert({ ...addForm, qr_code_url: qrUrl })
        .select()
        .single();
      if (error) throw error;
      setWallets((prev) => [...prev, data as CryptoWallet]);
      setAddStatus("success");
      setAddForm({ coin_name: "", coin_symbol: "", wallet_address: "", network: "" });
      setAddQrFile(null);
      setAddQrPreview(null);
      if (addFileRef.current) addFileRef.current.value = "";
      setTimeout(() => {
        setShowAddModal(false);
        setAddStatus("idle");
      }, 1000);
    } catch (err: unknown) {
      setAddStatus("error");
      setAddError(err instanceof Error ? err.message : "Failed to add wallet");
    }
  };

  const openEdit = (w: CryptoWallet) => {
    setEditingWallet(w);
    setEditForm({ coin_name: w.coin_name, coin_symbol: w.coin_symbol, wallet_address: w.wallet_address, network: w.network || "" });
    setEditQrFile(null);
    setEditQrPreview(null);
    if (editFileRef.current) editFileRef.current.value = "";
    setEditStatus("idle");
    setEditError("");
  };

  const handleEditQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditQrFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setEditQrPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveEditQr = () => {
    setEditQrFile(null);
    setEditQrPreview(null);
    // Mark that we want to clear the existing QR
    setEditForm({ ...editForm }); // force a state check
  };

  const handleEdit = async () => {
    if (!editingWallet) return;
    setEditStatus("loading");
    setEditError("");
    try {
      const supabase = getSupabase();
      let qrUrl: string | null | undefined = undefined;

      if (editQrFile) {
        qrUrl = await uploadQrCode(editQrFile);
      }

      const updateData: Record<string, unknown> = { ...editForm };
      if (qrUrl !== undefined) {
        updateData.qr_code_url = qrUrl;
      }

      const { error } = await supabase.from("crypto_wallets").update(updateData).eq("id", editingWallet.id);
      if (error) throw error;

      setEditStatus("success");
      setWallets((prev) =>
        prev.map((w) =>
          w.id === editingWallet.id
            ? { ...w, ...editForm, qr_code_url: qrUrl !== undefined ? qrUrl : w.qr_code_url }
            : w
        )
      );
      setTimeout(() => {
        setEditingWallet(null);
        setEditStatus("idle");
        setEditQrFile(null);
        setEditQrPreview(null);
      }, 1000);
    } catch (err: unknown) {
      setEditStatus("error");
      setEditError(err instanceof Error ? err.message : "Failed to update wallet");
    }
  };

  const handleRemoveQrCode = async (wallet: CryptoWallet) => {
    try {
      const supabase = getSupabase();
      // Try to remove from storage if it's a supabase URL
      if (wallet.qr_code_url) {
        const urlParts = wallet.qr_code_url.split("/");
        const filePath = urlParts.slice(urlParts.indexOf("wallet-qr") + 1).join("/");
        if (filePath) {
          await supabase.storage.from("wallet-qr").remove([filePath]);
        }
      }
      await supabase.from("crypto_wallets").update({ qr_code_url: null }).eq("id", wallet.id);
      setWallets((prev) =>
        prev.map((w) => (w.id === wallet.id ? { ...w, qr_code_url: null } : w))
      );
    } catch {
      // fallback — at least clear the DB
      const supabase = getSupabase();
      await supabase.from("crypto_wallets").update({ qr_code_url: null }).eq("id", wallet.id);
      setWallets((prev) =>
        prev.map((w) => (w.id === wallet.id ? { ...w, qr_code_url: null } : w))
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const supabase = getSupabase();
      const wallet = wallets.find((w) => w.id === id);
      if (wallet?.qr_code_url) {
        const urlParts = wallet.qr_code_url.split("/");
        const filePath = urlParts.slice(urlParts.indexOf("wallet-qr") + 1).join("/");
        if (filePath) {
          await supabase.storage.from("wallet-qr").remove([filePath]);
        }
      }
      await supabase.from("crypto_wallets").delete().eq("id", id);
      setWallets((prev) => prev.filter((w) => w.id !== id));
      setConfirmDeleteId(null);
    } catch {
      // fallback
    }
  };

  const coinIcons: Record<string, string> = {
    BTC: "fab fa-bitcoin text-amber-500",
    ETH: "fab fa-ethereum text-indigo-400",
    USDT: "fas fa-dollar-sign text-green-500",
    LTC: "fas fa-litecoin-sign text-gray-400",
    XMR: "fas fa-coins text-orange-500",
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Crypto Wallets</h1>
            <p className="text-xs text-gray-500">{wallets.length} wallets configured</p>
          </div>
          <button
            onClick={() => { setShowAddModal(true); setAddStatus("idle"); setAddError(""); setAddQrFile(null); setAddQrPreview(null); }}
            className="px-4 py-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
          >
            <i className="fas fa-plus mr-2" />Add Wallet
          </button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : wallets.length === 0 ? (
            <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-12 text-center">
              <i className="fas fa-wallet text-4xl text-gray-300 mb-3 block" />
              <p className="text-sm text-gray-500 mb-4">No crypto wallets configured yet.</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-plus mr-2" />Add Your First Wallet
              </button>
            </div>
          ) : (
            <div className="bg-[#0d1117] rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-gray-800">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Coin</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Symbol</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Network</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Wallet Address</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-24">QR Code</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {wallets.map((w) => (
                      <tr key={w.id} className="hover:bg-[#161b22]/50">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-amber-900/20 flex items-center justify-center">
                              <i className={`${coinIcons[w.coin_symbol] || "fab fa-bitcoin text-amber-500"} text-sm`} />
                            </div>
                            <span className="text-sm font-semibold text-gray-200">{w.coin_name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-xs font-semibold text-gray-500 bg-[#1c2128] rounded-md px-2.5 py-1">{w.coin_symbol}</span>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-gray-500">{w.network || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <code className="text-xs text-gray-300 bg-[#161b22] rounded px-2 py-1 block max-w-[200px] lg:max-w-sm font-mono truncate">{w.wallet_address}</code>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {w.qr_code_url ? (
                            <button
                              onClick={() => setViewingQr(w.qr_code_url)}
                              className="inline-flex items-center gap-1 text-xs text-[#5cb85c] hover:text-[#4cae4c] transition-all"
                            >
                              <i className="fas fa-qrcode" /> View
                            </button>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(w)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all" title="Edit">
                              <i className="fas fa-edit text-xs" />
                            </button>
                            <button onClick={() => setConfirmDeleteId(w.id)} className="w-7 h-7 rounded-lg hover:bg-red-900/20 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all" title="Delete">
                              <i className="fas fa-trash-alt text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Add Wallet Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Add Crypto Wallet</h3>
              <button onClick={() => setShowAddModal(false)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Coin Name *</label>
                  <input type="text" value={addForm.coin_name} onChange={(e) => setAddForm({ ...addForm, coin_name: e.target.value })} placeholder="Bitcoin" className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Symbol *</label>
                  <select value={addForm.coin_symbol} onChange={(e) => setAddForm({ ...addForm, coin_symbol: e.target.value })} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]">
                    <option value="">Select...</option>
                    <option value="BTC">BTC</option>
                    <option value="ETH">ETH</option>
                    <option value="USDT">USDT</option>
                    <option value="LTC">LTC</option>
                    <option value="XMR">XMR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Network</label>
                <select value={addForm.network} onChange={(e) => setAddForm({ ...addForm, network: e.target.value })} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select network...</option>
                  <option value="Bitcoin Network">Bitcoin Network</option>
                  <option value="ERC20">ERC20</option>
                  <option value="TRC20">TRC20</option>
                  <option value="BEP20">BEP20</option>
                  <option value="Litecoin Network">Litecoin Network</option>
                  <option value="Monero Network">Monero Network</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Wallet Address *</label>
                <textarea value={addForm.wallet_address} onChange={(e) => setAddForm({ ...addForm, wallet_address: e.target.value })} rows={3} placeholder="Enter wallet address..." className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c] resize-none font-mono" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">QR Code Image</label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-[#161b22] border border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-[#5cb85c] transition-all">
                    <i className="fas fa-upload text-gray-400 text-xs" />
                    <span className="text-xs text-gray-400 truncate">{addQrFile ? addQrFile.name : "Choose image..."}</span>
                    <input ref={addFileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleAddQrFileChange} className="hidden" />
                  </label>
                  {addQrPreview && (
                    <button onClick={() => { setAddQrFile(null); setAddQrPreview(null); if (addFileRef.current) addFileRef.current.value = ""; }} className="w-7 h-7 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-all" title="Remove QR">
                      <i className="fas fa-times text-xs" />
                    </button>
                  )}
                </div>
                {addQrPreview && (
                  <div className="mt-2 w-20 h-20 rounded-lg border border-gray-700 overflow-hidden bg-white p-1">
                    <img src={addQrPreview} alt="QR preview" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>
            {addStatus === "error" && <p className="mt-3 text-xs text-red-500">{addError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={handleAdd} disabled={addStatus === "loading"} className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">
                {addStatus === "loading" ? "Adding..." : addStatus === "success" ? "Added!" : "Add Wallet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Wallet Modal */}
      {editingWallet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditingWallet(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Edit Wallet</h3>
              <button onClick={() => setEditingWallet(null)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Coin Name</label>
                  <input type="text" value={editForm.coin_name} onChange={(e) => setEditForm({ ...editForm, coin_name: e.target.value })} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Symbol</label>
                  <select value={editForm.coin_symbol} onChange={(e) => setEditForm({ ...editForm, coin_symbol: e.target.value })} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]">
                    <option value="">Select...</option>
                    <option value="BTC">BTC</option>
                    <option value="ETH">ETH</option>
                    <option value="USDT">USDT</option>
                    <option value="LTC">LTC</option>
                    <option value="XMR">XMR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Network</label>
                <select value={editForm.network} onChange={(e) => setEditForm({ ...editForm, network: e.target.value })} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select network...</option>
                  <option value="Bitcoin Network">Bitcoin Network</option>
                  <option value="ERC20">ERC20</option>
                  <option value="TRC20">TRC20</option>
                  <option value="BEP20">BEP20</option>
                  <option value="Litecoin Network">Litecoin Network</option>
                  <option value="Monero Network">Monero Network</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Wallet Address</label>
                <textarea value={editForm.wallet_address} onChange={(e) => setEditForm({ ...editForm, wallet_address: e.target.value })} rows={3} className="w-full px-3 py-2 bg-[#161b22] border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#5cb85c] resize-none font-mono" />
              </div>

              {/* Current QR Code */}
              {editingWallet.qr_code_url && !editQrPreview && (
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">Current QR Code</label>
                  <div className="flex items-start gap-3">
                    <div className="w-20 h-20 rounded-lg border border-gray-700 overflow-hidden bg-white p-1 flex-shrink-0">
                      <img src={editingWallet.qr_code_url} alt="Current QR" className="w-full h-full object-contain" />
                    </div>
                    <button onClick={handleRemoveEditQr} className="text-xs text-red-400 hover:text-red-300 transition-all whitespace-nowrap">
                      <i className="fas fa-trash-alt mr-1" /> Remove
                    </button>
                  </div>
                </div>
              )}

              {/* New QR Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">
                  {editQrPreview ? "New QR Code" : editingWallet.qr_code_url ? "Replace QR Code" : "QR Code Image"}
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-[#161b22] border border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-[#5cb85c] transition-all">
                    <i className="fas fa-upload text-gray-400 text-xs" />
                    <span className="text-xs text-gray-400 truncate">{editQrFile ? editQrFile.name : "Choose image..."}</span>
                    <input ref={editFileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleEditQrFileChange} className="hidden" />
                  </label>
                  {editQrPreview && (
                    <button onClick={() => { setEditQrFile(null); setEditQrPreview(null); if (editFileRef.current) editFileRef.current.value = ""; }} className="w-7 h-7 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-all" title="Remove QR">
                      <i className="fas fa-times text-xs" />
                    </button>
                  )}
                </div>
                {editQrPreview && (
                  <div className="mt-2 w-20 h-20 rounded-lg border border-gray-700 overflow-hidden bg-white p-1">
                    <img src={editQrPreview} alt="New QR preview" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>
            {editStatus === "error" && <p className="mt-3 text-xs text-red-500">{editError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingWallet(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={handleEdit} disabled={editStatus === "loading"} className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">
                {editStatus === "loading" ? "Saving..." : editStatus === "success" ? "Saved!" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-200 text-center mb-2">Delete Wallet?</h3>
            <p className="text-xs text-gray-500 text-center mb-5">This wallet address will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Viewer Modal */}
      {viewingQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setViewingQr(null)}>
          <div className="bg-white rounded-xl p-4 max-w-xs mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-800">Wallet QR Code</span>
              <button onClick={() => setViewingQr(null)} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <img src={viewingQr} alt="Wallet QR Code" className="w-full aspect-square object-contain rounded-lg" />
            <button
              onClick={() => setViewingQr(null)}
              className="w-full mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}